import React from 'react';
import { useAvContext } from '@context/AvContextProvider';
import {
  AddOperandButton,
  boxWidth,
  cleanValueFromExpression,
  Filter,
  filterOperatorOptions,
  getEmptyFilterExpression,
  getRootOperator,
  isIngressExp,
  isNullOperator,
  OperatorType,
  RelationsProps,
  shouldRenderWithFieldFilterBox,
  StringConditionType,
} from '@fabric-ui/components';
import { flex } from '@fabric-ui/theme';
import { emptyObject } from '@fabric-ui/utils';
import { Box } from '@mui/material';
import { EntityProjectionName } from '@utils/entityProjectionNames';
import { isNullOrUndefined } from '@utils/utils';
import FieldFilterBox, { FieldFilterBoxProps } from './FieldFilterBox';

interface RenderOperatorProps {
  filter: Filter;
  setFilter: any;
  fields: any[];
  isLoadingFields?: boolean;
  isVertical: boolean;
  size: 'xSmall' | 'small';
  defaultField: string | null;
  rootOperator: 'or' | 'and';
  level?: number;
  onToggleLogicalOperators: () => void;
  canAddNewFields?: boolean;
  canSelectFieldType?: boolean;
  supportParser?: boolean;
  timeRange?: Date[];
  canDeleteLast?: boolean;
  operatorOptions?: typeof filterOperatorOptions;
  ingressModeProps?: FieldFilterBoxProps['ingressModeProps'];
  relationsProps: RelationsProps;
  disableMessage?: string;
  isOverExpressionsLimit?: boolean;
  fieldSelectionMessage?: string;
  fieldDescription?: string;
  fieldOnly?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  fieldsListAdornment?: React.ReactNode;
  hasListTab?: boolean;
  activeProjName?: string;
  icon?: React.ReactNode;
  isHistorical?: boolean;
  showAdvanced?: boolean;
  shouldShowIcon?: (operand: any) => boolean;
  defaultMode?: 'list' | 'condition';
  excludeNullFromList?: boolean;
  enableQueryObj?: boolean;
  fieldExtraOptions?: Record<string, string[]>;
  tokenSuggestions?: FieldFilterBoxProps['tokenSuggestions'];
}

export const hasEmptyValueOperand = (filter: Filter): boolean => {
  const operator = getRootOperator(filter);
  if (operator === 'and' || operator === 'or') {
    const operands = filter[operator]?.operands;
    return !!operands?.some(op => {
      const condition = op.expression?.stringCondition;
      return condition && Object.values(condition).some(v => v === '');
    });
  }
  return false;
};

export const hasMultipleInOperands = (filter: Filter): boolean => {
  const operator = getRootOperator(filter);
  if (operator !== 'and' && operator !== 'or') return false;
  const operands = filter[operator]?.operands;
  if (!operands || operands.length <= 1) return false;
  return operands.filter(op => op.expression?.stringCondition?.in).length > 1;
};

const RenderOperator: React.FC<RenderOperatorProps> = ({
  filter,
  setFilter,
  rootOperator,
  size,
  fields,
  isLoadingFields,
  isVertical,
  level = 0,
  defaultField,
  onToggleLogicalOperators,
  canAddNewFields,
  canSelectFieldType,
  supportParser,
  timeRange,
  canDeleteLast,
  operatorOptions,
  ingressModeProps,
  relationsProps = emptyObject as RelationsProps,
  disableMessage,
  isOverExpressionsLimit,
  fieldSelectionMessage,
  fieldOnly = false,
  disabled = false,
  readOnly = false,
  fieldsListAdornment,
  hasListTab = false,
  activeProjName,
  icon,
  isHistorical,
  showAdvanced,
  shouldShowIcon,
  defaultMode,
  excludeNullFromList,
  enableQueryObj = false,
  fieldExtraOptions,
  tokenSuggestions,
  fieldDescription,
}) => {
  const {
    accountEntities: { fieldMap },
  } = useAvContext();

  const operatorColors = {
    and: 'var(--color-surface-interactive-primary-default)',
    or: 'var(--color-border-status-warning-secondary)',
  };

  const commonProps = {
    size,
    isVertical,
    fields,
    isLoadingFields,
    canAddNewFields,
    canSelectFieldType,
    supportParser,
    operatorOptions,
    relationsProps,
    disableMessage,
    isOverExpressionsLimit,
    fieldSelectionMessage,
    fieldDescription,
    fieldOnly,
    disabled,
    fieldsListAdornment,
    hasListTab,
    activeProjName,
    isHistorical,
    showAdvanced,
    shouldShowIcon,
    defaultMode,
    excludeNullFromList,
    enableQueryObj,
    fieldExtraOptions,
  };

  const operandsList = filter[rootOperator]?.operands;
  return operandsList?.map((innerFilter, index) => {
    const oppositeOperator = rootOperator === OperatorType.OR ? OperatorType.AND : OperatorType.OR;
    const setInnerFilter = value => {
      const operands = operandsList.map((v, i) => (i === index ? value : v));
      setFilter({ [rootOperator]: { operands } });
    };

    const onDelete = () => {
      const operands = operandsList.filter((v, i) => i !== index);
      if (operands.length === 0 && level === 0) {
        setFilter(
          canDeleteLast
            ? undefined
            : relationsProps.defaultEmptyFilter
              ? { and: { operands: [relationsProps.defaultEmptyFilter] } }
              : getEmptyFilterExpression(defaultField, supportParser ? StringConditionType.EQUALS : undefined)
        );
      } else if (operands.length === 1 && (level !== 0 || !operands[0].expression)) {
        if (level === 0 && shouldRenderWithFieldFilterBox(operands[0], hasListTab) && !operands[0].expression) {
          setFilter({ [rootOperator]: { operands } });
        } else {
          setFilter(operands[0]);
        }
      } else {
        setFilter({ [rootOperator]: { operands } });
      }
    };

    const getOperands = () => {
      const { operands } = filter[rootOperator]!;
      const cleaned = isIngressExp(operands[index]) ? operands[index] : cleanValueFromExpression(operands[index], fieldMap);
      const condition = cleaned.expression?.stringCondition;
      const newItem =
        condition && Object.keys(condition).some(k => isNullOperator(k))
          ? { expression: { fieldName: cleaned.expression?.fieldName, stringCondition: { equals: '' } } }
          : cleaned;

      if (level === 0) {
        return operands.with(index, { [oppositeOperator]: { operands: [operands[index], newItem] } });
      }
      return operands.toSpliced(index + 1, 0, newItem);
    };

    const addLogicalExpression = () => setFilter({ [rootOperator]: { operands: getOperands() } });
    const isIngressExpression = isIngressExp(innerFilter); // TODO - add projection as prop to the component?

    return (
      // eslint-disable-next-line react/no-array-index-key
      <Box key={index} sx={{ borderLeft: `2px solid ${operatorColors[rootOperator]}`, ml: level * 2 }}>
        {index > 0 && operandsList.length > index && (
          <Box
            sx={{
              ...flex.itemsStart,
              my: '-4px',
              ml: isVertical ? `${boxWidth / 2}px` : `${boxWidth / 4}px`,
            }}>
            <AddOperandButton
              style={{ translate: '-50%' }}
              size={size}
              operator={rootOperator}
              onClick={onToggleLogicalOperators}
              disabled={disabled}
            />
          </Box>
        )}
        {shouldRenderWithFieldFilterBox(innerFilter, hasListTab) &&
        !(hasListTab && hasEmptyValueOperand(innerFilter)) &&
        !(hasListTab && hasMultipleInOperands(innerFilter)) ? (
          <Box sx={{ ...flex.justifyStartCenter, gap: 1 }}>
            <FieldFilterBox
              {...commonProps}
              timeRange={timeRange}
              rootOperator={rootOperator}
              setFilter={setInnerFilter}
              level={level}
              filter={innerFilter}
              onDelete={onDelete}
              addLogicalExpression={fieldOnly || operandsList.length === 1 ? undefined : addLogicalExpression}
              canSelectFieldType={canSelectFieldType}
              ingressModeProps={ingressModeProps}
              isIngressExp={isIngressExpression}
              defaultField={defaultField ?? undefined}
              readOnly={readOnly}
              activeProjName={activeProjName as EntityProjectionName}
              tokenSuggestions={tokenSuggestions}
            />
            {(isNullOrUndefined(shouldShowIcon) || shouldShowIcon?.(innerFilter)) && icon}
          </Box>
        ) : (
          <RenderOperator
            {...commonProps}
            timeRange={timeRange}
            defaultField={defaultField}
            filter={innerFilter}
            setFilter={setInnerFilter}
            rootOperator={getRootOperator(innerFilter) as 'and' | 'or'}
            level={level + 1}
            onToggleLogicalOperators={onToggleLogicalOperators}
            ingressModeProps={ingressModeProps}
            icon={icon}
            tokenSuggestions={tokenSuggestions}
          />
        )}
      </Box>
    );
  });
};

export default RenderOperator;
